import api from './api';
import type {
  Addon,
  CurrencyCode,
  MinutePack,
  MinutePackCatalog,
  SubscriptionAddon,
  SubscriptionPlan,
  SubscriptionPlanType,
  SubscriptionSummary,
  SubscriptionUsagePeriodStatus,
  SubscriptionUsagePeriodSummary,
} from '../types';

const CURRENT_SUBSCRIPTION_DEFAULT_KEY = '__default__';
const currentSubscriptionRequests = new Map<string, Promise<SubscriptionSummary>>();

function buildCurrentSubscriptionRequestKey(subscriptionType?: SubscriptionPlanType): string {
  return subscriptionType ?? CURRENT_SUBSCRIPTION_DEFAULT_KEY;
}

/**
 * Reuses an in-flight current-subscription request for the same plan type.
 * This prevents layout/page bootstrap from issuing duplicate GETs before the
 * first response settles.
 */
export async function fetchCurrentSubscription(subscriptionType?: SubscriptionPlanType): Promise<SubscriptionSummary> {
  const requestKey = buildCurrentSubscriptionRequestKey(subscriptionType);
  const inFlightRequest = currentSubscriptionRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = api.get('/api/subscriptions/current/', {
    params: subscriptionType ? { subscription_type: subscriptionType } : undefined,
  })
    .then((response) => response.data as SubscriptionSummary)
    .finally(() => {
      currentSubscriptionRequests.delete(requestKey);
    });

  currentSubscriptionRequests.set(requestKey, request);
  return request;
}

export interface FetchSubscriptionUsagePeriodsParams {
  tenantId?: string;
  subscriptionId?: string;
  status?: SubscriptionUsagePeriodStatus;
  periodStartFrom?: string;
  periodStartTo?: string;
  periodEndFrom?: string;
  periodEndTo?: string;
  ordering?: string;
}

export async function fetchSubscriptionUsagePeriods(
  params: FetchSubscriptionUsagePeriodsParams = {}
): Promise<SubscriptionUsagePeriodSummary[]> {
  const response = await api.get('/api/subscriptions/usage-periods/', {
    params: {
      tenant_id: params.tenantId,
      subscription_id: params.subscriptionId,
      status: params.status,
      period_start_from: params.periodStartFrom,
      period_start_to: params.periodStartTo,
      period_end_from: params.periodEndFrom,
      period_end_to: params.periodEndTo,
      ordering: params.ordering,
    },
  });
  return response.data as SubscriptionUsagePeriodSummary[];
}

export async function fetchAvailableAddons(): Promise<Addon[]> {
  const response = await api.get('/api/subscriptions/addons/catalog/');
  return response.data as Addon[];
}

export async function fetchMinutePackCatalog(): Promise<MinutePackCatalog[]> {
  const response = await api.get('/api/subscriptions/minute-packs/catalog/');
  return response.data as MinutePackCatalog[];
}

export async function purchaseMinutePack(data: {
  packId: string;
  priceAmount?: number;
}): Promise<MinutePack> {
  const response = await api.post('/api/minute-packs/purchase/', data);
  return response.data as MinutePack;
}

export async function activateSubscriptionAddon(data: {
  addonId?: string;
  addonCode?: string;
  subscriptionType?: SubscriptionPlanType;
  quantity: number;
  priceAmount?: number;
  autoRenew: boolean;
}): Promise<SubscriptionAddon> {
  const response = await api.post('/api/subscriptions/addons/activate/', data);
  return response.data as SubscriptionAddon;
}

export async function cancelSubscriptionAddon(subscriptionAddonId: string): Promise<SubscriptionAddon> {
  const response = await api.post('/api/subscriptions/addons/cancel/', {
    subscriptionAddonId,
  });
  return response.data as SubscriptionAddon;
}

export async function fetchPlansAdmin(): Promise<SubscriptionPlan[]> {
  const response = await api.get('/api/subscriptions/plans/');
  return response.data as SubscriptionPlan[];
}

export async function fetchPlanAdmin(planId: string): Promise<SubscriptionPlan> {
  const response = await api.get(`/api/subscriptions/plans/${planId}/`);
  return response.data as SubscriptionPlan;
}

export interface PlanAdminPayload {
  name: string;
  description?: string;
  plan_type: SubscriptionPlanType;
  price_amount: number;
  price_currency: CurrencyCode;
  setup_fee_amount: number;
  overage_rate_amount: number;
  billing_interval: SubscriptionPlan['billingInterval'];
  max_minutes_seconds: number;
  max_concurrent_calls: number;
  included_users: number;
  included_ai_agents: number;
  included_whatsapp_accounts: number;
  enable_public_api: boolean;
  is_active: boolean;
}

export async function createPlanAdmin(payload: PlanAdminPayload): Promise<SubscriptionPlan> {
  const response = await api.post('/api/subscriptions/plans/', payload);
  return response.data as SubscriptionPlan;
}

export async function updatePlanAdmin(
  planId: string,
  payload: Partial<PlanAdminPayload>
): Promise<SubscriptionPlan> {
  const response = await api.patch(`/api/subscriptions/plans/${planId}/`, payload);
  return response.data as SubscriptionPlan;
}

export async function deletePlanAdmin(planId: string): Promise<void> {
  await api.delete(`/api/subscriptions/plans/${planId}/`);
}

export async function fetchAddonsAdmin(): Promise<Addon[]> {
  const response = await api.get('/api/subscriptions/addons/');
  return response.data as Addon[];
}

export async function fetchAddonAdmin(addonId: string): Promise<Addon> {
  const response = await api.get(`/api/subscriptions/addons/${addonId}/`);
  return response.data as Addon;
}

export interface AddonAdminPayload {
  code: Addon['code'];
  name: string;
  description?: string;
  price_amount: number;
  price_currency: CurrencyCode;
  is_active: boolean;
}

export async function createAddonAdmin(payload: AddonAdminPayload): Promise<Addon> {
  const response = await api.post('/api/subscriptions/addons/', payload);
  return response.data as Addon;
}

export async function updateAddonAdmin(
  addonId: string,
  payload: Partial<AddonAdminPayload>
): Promise<Addon> {
  const response = await api.patch(`/api/subscriptions/addons/${addonId}/`, payload);
  return response.data as Addon;
}

export async function deleteAddonAdmin(addonId: string): Promise<void> {
  await api.delete(`/api/subscriptions/addons/${addonId}/`);
}

export async function fetchMinutePacksAdmin(): Promise<MinutePackCatalog[]> {
  const response = await api.get('/api/subscriptions/minute-packs/');
  return response.data as MinutePackCatalog[];
}

export async function fetchMinutePackAdmin(packId: string): Promise<MinutePackCatalog> {
  const response = await api.get(`/api/subscriptions/minute-packs/${packId}/`);
  return response.data as MinutePackCatalog;
}

export interface MinutePackAdminPayload {
  type: MinutePack['type'];
  minutes_quantity: number;
  price_amount?: number;
  price_currency: CurrencyCode;
  is_active: boolean;
}

export interface TenantSubscriptionAdminItem {
  id: string;
  tenantId: string;
  planId: string;
  planName: string;
  planType: SubscriptionPlanType;
  planPriceAmount: number;
  planPriceAmountOverride?: number | null;
  planCatalogPriceAmount: number;
  planPriceCurrency: CurrencyCode;
  status: 'active' | 'past_due' | 'canceled' | 'inactive';
  startedAt: string;
  currentPeriodStart: string;
  currentPeriodEnd: string | null;
  minutesUsedSeconds: number;
  overageAiSeconds: number;
  overageTelephonySeconds: number;
  enablePublicApi: boolean;
}

export async function fetchTenantSubscriptionsAdmin(tenantId: string): Promise<TenantSubscriptionAdminItem[]> {
  const response = await api.get(`/api/tenants/${tenantId}/subscriptions/`);
  return response.data as TenantSubscriptionAdminItem[];
}

export async function assignTenantSubscriptionAdmin(payload: {
  tenantId: string;
  planId: string;
  replaceActive?: boolean;
  planPriceAmountOverride?: number | null;
}): Promise<TenantSubscriptionAdminItem> {
  const response = await api.post(`/api/tenants/${payload.tenantId}/subscriptions/`, {
    plan_id: payload.planId,
    replace_active: payload.replaceActive ?? true,
    plan_price_amount_override: payload.planPriceAmountOverride,
  });
  return response.data as TenantSubscriptionAdminItem;
}

export async function fetchTenantMinutePacksAdmin(tenantId: string): Promise<MinutePack[]> {
  const response = await api.get(`/api/tenants/${tenantId}/minute-packs/`);
  return response.data as MinutePack[];
}

export async function createTenantMinutePackAdmin(
  tenantId: string,
  payload: {
    packId?: string;
    subscriptionType?: SubscriptionPlanType;
    type?: MinutePack['type'];
    minutesQuantity?: number;
    priceAmount?: number;
    purchasedAt?: string;
    expiresAt?: string;
    isActive?: boolean;
  }
): Promise<MinutePack> {
  const { packId, subscriptionType, type, minutesQuantity, priceAmount, purchasedAt, expiresAt, isActive } = payload;
  const response = await api.post(`/api/tenants/${tenantId}/minute-packs/`, {
    pack_id: packId,
    subscription_type: subscriptionType,
    type,
    minutes_quantity: minutesQuantity,
    price_amount: priceAmount,
    purchased_at: purchasedAt,
    expires_at: expiresAt,
    is_active: isActive,
  });
  return response.data as MinutePack;
}

export async function fetchTenantAddonsAdmin(tenantId: string): Promise<SubscriptionAddon[]> {
  const response = await api.get(`/api/tenants/${tenantId}/addons/`);
  return response.data as SubscriptionAddon[];
}

export async function activateTenantAddonAdmin(
  tenantId: string,
  data: {
    addonId?: string;
    addonCode?: string;
    subscriptionId?: string;
    subscriptionType?: SubscriptionPlanType;
    quantity: number;
    priceAmount?: number;
    autoRenew: boolean;
  }
): Promise<SubscriptionAddon> {
  const response = await api.post(`/api/tenants/${tenantId}/addons/`, data);
  return response.data as SubscriptionAddon;
}

export async function cancelTenantAddonAdmin(
  tenantId: string,
  subscriptionAddonId: string
): Promise<SubscriptionAddon> {
  const response = await api.post(`/api/tenants/${tenantId}/addons/cancel/`, {
    subscription_addon_id: subscriptionAddonId,
  });
  return response.data as SubscriptionAddon;
}

export async function createMinutePackAdmin(payload: MinutePackAdminPayload): Promise<MinutePackCatalog> {
  const response = await api.post('/api/subscriptions/minute-packs/', payload);
  return response.data as MinutePackCatalog;
}

export async function updateMinutePackAdmin(
  packId: string,
  payload: Partial<MinutePackAdminPayload>
): Promise<MinutePackCatalog> {
  const response = await api.patch(`/api/subscriptions/minute-packs/${packId}/`, payload);
  return response.data as MinutePackCatalog;
}

export async function deleteMinutePackAdmin(packId: string): Promise<void> {
  await api.delete(`/api/subscriptions/minute-packs/${packId}/`);
}
